import React from "react";
import { toast } from "sonner";
import { CalendarRange, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const EMPTY_EVENT = { title: "", description: "", event_type: "event", start_date: "", end_date: "", start_time: "", end_time: "", all_day: true, location: "", class_id: "" };

export function EventFormDialog({ form, setForm, onClose, onSaved }) {
  const editId = form?.id;
  const save = async () => {
    if (!form.title.trim() || !form.start_date) { toast.error("Add a title and date"); return; }
    try {
      if (editId) await api.put(`/events/${editId}`, form); else await api.post("/events", form);
      toast.success("Event saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"?`)) return;
    try { await api.delete(`/events/${editId}`); toast.success("Event removed"); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete event"); }
  };
  const f = (k) => ({ value: form[k] || "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <Dialog open={!!form} onOpenChange={(v) => !v && onClose()}>
      {form && <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><CalendarRange className="w-5 h-5 text-teal" />{editId ? "Edit" : "New"} Event</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input data-testid="event-title" {...f("title")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Type</Label>
              <select data-testid="event-type" className="w-full border rounded-md h-10 px-3" {...f("event_type")}>
                <option value="event">Event</option><option value="recital">Recital</option><option value="holiday">Holiday / Closure</option>
              </select></div>
            <div className="space-y-1.5"><Label>Location</Label><Input data-testid="event-location" {...f("location")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label><Input data-testid="event-start-date" type="date" {...f("start_date")} /></div>
            <div className="space-y-1.5"><Label>End date (optional)</Label><Input data-testid="event-end-date" type="date" {...f("end_date")} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="event-all-day" checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} />All day</label>
          {!form.all_day && <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start time</Label><Input data-testid="event-start-time" type="time" {...f("start_time")} /></div>
            <div className="space-y-1.5"><Label>End time</Label><Input data-testid="event-end-time" type="time" {...f("end_time")} /></div>
          </div>}
          <div className="space-y-1.5"><Label>Description / preparation</Label><Textarea data-testid="event-description" {...f("description")} /></div>
          {form.event_type === "holiday" && <p className="text-xs text-orange-700 bg-orange-50 rounded-lg p-2.5">Regular classes are hidden from the calendar on closure days.</p>}
          {form.event_type === "recital" && <p className="text-xs text-teal-dark bg-teal-light rounded-lg p-2.5">This recital will automatically power the countdown on every home screen when it's the next upcoming one.</p>}
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          {editId ? <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-event-button" onClick={remove}><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button> : <span />}
          <Button data-testid="save-event-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Event</Button>
        </DialogFooter>
      </DialogContent>}
    </Dialog>);
}
